// What Dana is told about the device she is answering on (Dana & MCP v2.1
// Phase 3: "installed-app guidance" — correct steps for the device someone is
// actually on, never an inaccurate install instruction or an offline promise).
//
// ⚠️ DANA DOES NOT DECIDE ANY OF THIS. The verdict is installState's — the
// same one the install panel shows — so Dana and the screen can never
// disagree, and every rule there (Edge is not Chrome, no event ≠ not
// installable, phones get the store app) holds for her too. She receives the
// mode and the verified steps verbatim; when there are none, she has none.
import { getWaitingWorker } from '../recovery/registerWorker';
import { getCapturedPrompt, wasInstalledThisSession } from './installCapture';
import {
  chromiumFlavour, installMode, isDesktopSafari, isStandalone, manualInstructions, type InstallMode,
} from './installState';

export interface DanaClientContext {
  surface: 'web';
  platform: 'ios' | 'android' | 'desktop';
  browser: 'edge' | 'chrome' | 'safari' | 'other';
  /** installState's verdict for THIS browser. */
  install_mode: InstallMode;
  /** Verified menu steps for this browser (manual mode only), verbatim. */
  install_steps?: string;
  /** Running as the installed app in this context. */
  installed: boolean;
  /** A KaziPeople service worker controls this page — only then does an
   *  update arrive as an Update prompt (pilot companies); otherwise a new
   *  version simply loads on the next refresh. */
  worker_active: boolean;
  /** A new version is downloaded and waiting for the person's go-ahead. */
  update_waiting: boolean;
  /** The workspace does not work offline (only a notice page shows). */
  offline_supported: false;
}

export interface ClientEnv {
  ua: string;
  standalone: boolean;
  hasPromptEvent: boolean;
  installedThisSession: boolean;
  updateWaiting: boolean;
  workerActive: boolean;
}

export const readClientEnv = (): ClientEnv => ({
  ua: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  standalone: isStandalone(),
  hasPromptEvent: !!getCapturedPrompt(),
  installedThisSession: wasInstalledThisSession(),
  updateWaiting: !!getWaitingWorker(),
  workerActive: typeof navigator !== 'undefined' && !!navigator.serviceWorker?.controller,
});

/** Pure, so the mapping is testable without a browser. */
export const danaClientContext = (env: ClientEnv = readClientEnv()): DanaClientContext => {
  const ua = env.ua;
  const platform = /iPad|iPhone|iPod/.test(ua) ? 'ios' : /Android/.test(ua) ? 'android' : 'desktop';
  const flavour = chromiumFlavour(ua);
  const browser = flavour ?? (isDesktopSafari(ua) || (platform === 'ios' && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)) ? 'safari' : 'other');
  const installed = env.standalone || env.installedThisSession;
  const mode = installMode({ hasPromptEvent: env.hasPromptEvent, standalone: installed, ua });
  const steps = mode === 'manual' ? manualInstructions(ua)?.steps : undefined;
  return {
    surface: 'web', platform, browser, install_mode: mode,
    ...(steps ? { install_steps: steps } : {}),
    installed, worker_active: env.workerActive, update_waiting: env.updateWaiting, offline_supported: false,
  };
};
